import { useState, useRef, useEffect } from "react";

// Infinite-loop scroll for a horizontally scrollable track — desktop only.
// Renders 3 copies of items; starts at the middle copy.
// Returns whether looping is active (so the caller can conditionally render 3 copies).
function useInfiniteTrack(trackRef, resetKey) {
  const [looping, setLooping] = useState(false);
  const loopingRef = useRef(false);

  // Only activate on desktop (tablet-wide = 1024px+)
  const isDesktop = () => window.innerWidth >= 1024;

  // Check whether the track overflows (looping needed) and reset scroll
  useEffect(() => {
    setLooping(false);
    loopingRef.current = false;
    if (!trackRef) return;
    const track = trackRef.current;
    if (!track) return;
    track.scrollLeft = 0;

    const check = () => {
      if (!isDesktop()) return;
      const overflows = track.scrollWidth > track.clientWidth + 1;
      if (overflows && !loopingRef.current) {
        loopingRef.current = true;
        setLooping(true);
      }
    };

    const id = requestAnimationFrame(check);
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(check, 200);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimer);
    };
  }, [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Jump to the middle copy when looping activates
  useEffect(() => {
    if (!trackRef) return;
    const track = trackRef.current;
    if (!track || !looping) return;
    track.scrollLeft = track.scrollWidth / 3;
  }, [looping]); // eslint-disable-line react-hooks/exhaustive-deps

  // Edge detection via scroll event
  useEffect(() => {
    if (!trackRef) return;
    const track = trackRef.current;
    if (!track || !looping) return;

    let jumping = false;
    const handleScroll = () => {
      if (jumping) return;
      const third = track.scrollWidth / 3;
      if (track.scrollLeft < third * 0.4) {
        jumping = true;
        track.scrollLeft += third;
        setTimeout(() => {
          jumping = false;
        }, 150);
      } else if (track.scrollLeft > third * 1.6) {
        jumping = true;
        track.scrollLeft -= third;
        setTimeout(() => {
          jumping = false;
        }, 150);
      }
    };

    track.addEventListener("scroll", handleScroll, { passive: true });
    return () => track.removeEventListener("scroll", handleScroll);
  }, [looping]); // eslint-disable-line react-hooks/exhaustive-deps

  return looping;
}

export default useInfiniteTrack;
